import type {
  DailyTacticalImprovementProposal,
  DailyTacticalImprovementProposals,
  DailyTacticalReportHistory,
} from '../../services/operationsApi';

export default function DailyTacticalReportVisibility({
  history,
  proposals,
  historyError,
  proposalsError,
  variant,
}: Props) {
  const prefix = variant === 'fable' ? 'fable' : 'atu2';
  const reports = history?.items ?? [];
  const proposalItems = proposals?.items ?? [];
  const userFacing = variant === 'fable';

  return (
    <section className={`${prefix}-panel ${prefix}-report-visibility`}>
      <div className={`${prefix}-section-head ${prefix}-report-head`}>
        <div>
          <span className={`${prefix}-kicker`}>{userFacing ? '자동 저장 결과' : 'Persisted reports'}</span>
          <h2>{userFacing ? '매일 자동으로 남긴 보고서' : 'Daily Tactical 리포트 기록'}</h2>
          <p>{userFacing ? '실행 결과가 빠짐없이 저장됐는지와 개선 제안의 승인 상태를 확인합니다.' : '선택된 cohort에 저장된 리포트와 승인 상태를 조회합니다.'}</p>
        </div>
        <span className={`${prefix}-report-state ${reports.length ? 'generated' : 'missing'}`}>
          {reports.length ? `생성됨 · ${reports.length}건` : '미생성 · 기록 없음'}
        </span>
      </div>

      {historyError ? (
        <p className={`${prefix}-error`}>리포트 기록 조회 실패: {historyError}</p>
      ) : reports.length ? (
        <div className={`${prefix}-report-table-wrap`}>
          <table className={`${prefix}-report-table`}>
            <thead><tr><th>거래일</th><th>산출물</th><th>저장 시각</th></tr></thead>
            <tbody>{reports.map((item) => (
              <tr key={item.id}>
                <td>{item.trading_date}</td>
                <td>{userFacing ? artifactLabel(item.artifact_type) : <code>{item.artifact_type}</code>}</td>
                <td>{formatDate(item.created_at)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : (
        <p className={`${prefix}-report-empty`}>아직 저장된 일일 운영 보고서가 없습니다.</p>
      )}

      <div className={`${prefix}-proposal-head`}>
        <div>
          <h3>개선 제안</h3>
          <p>승인된 제안만 운영 반영 대상입니다. 이 화면에서는 상태를 변경할 수 없습니다.</p>
        </div>
        <span>{proposalItems.length}건</span>
      </div>
      {proposalsError ? (
        <p className={`${prefix}-error`}>개선 제안 조회 실패: {proposalsError}</p>
      ) : proposalItems.length ? (
        <div className={`${prefix}-proposal-list`}>
          {proposalItems.map((proposal) => <Proposal key={proposal.id} proposal={proposal} prefix={prefix} />)}
        </div>
      ) : (
        <p className={`${prefix}-report-empty`}>등록된 개선 제안이 없습니다.</p>
      )}
    </section>
  );
}

function Proposal({ proposal, prefix }: { proposal: DailyTacticalImprovementProposal; prefix: string }) {
  const approved = proposal.status.toLowerCase() === 'approved';
  return (
    <article className={`${prefix}-proposal`}>
      <div>
        <strong>{proposal.title}</strong>
        <span className={`${prefix}-proposal-status ${approved ? 'approved' : 'gated'}`}>
          {approved ? '승인됨' : `승인 대기 · ${proposal.status}`}
        </span>
      </div>
      <p>{proposal.rationale}</p>
      <dl>
        <dt>검증 계획</dt><dd>{proposal.validation_plan}</dd>
        <dt>제안 키</dt><dd><code>{proposal.proposal_key}</code></dd>
      </dl>
    </article>
  );
}

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ko-KR');
}

function artifactLabel(value: string) {
  const labels: Record<string, string> = {
    daily_ops_report: '일일 운영 점검',
    pre_economic_integrity: '손익 계산 전 절차 점검',
    model_rerun_audit: 'AI 판단 재현 점검',
  };
  return labels[value] || value;
}

type Props = {
  history: DailyTacticalReportHistory | null;
  proposals: DailyTacticalImprovementProposals | null;
  historyError: string | null;
  proposalsError: string | null;
  variant: 'atu2' | 'fable';
};
